import { useEffect, useState } from "react";
import { Form, Button, Row, Container, Col } from "react-bootstrap";
import { GenericForm } from "../../Component/GenericForm/GenericForm";
import { Api } from "../../Helpers/axios/axios";
import { formInterface } from "../../Component/GenericForm/formInterface";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "../../Component/Common/Headers";
import { LoadingSpinner } from "../../Component/Spinner/LoadingSpinner";
import { SeoFormSection } from "../../Component/Common/SeoFormSection";

const ProgramContentSection = ({ data, onChange, field, label }: any) => {
    const content = data?.[field] || { title: '', paragraphs: [''] };
    const paragraphs = content.paragraphs?.length > 0 ? content.paragraphs : [''];

    const updateParagraphs = (nextParagraphs: string[]) => {
        onChange(field, { ...content, paragraphs: nextParagraphs });
    };

    return (
        <Col md={12} className="mb-4">
            <Form.Label className="generic-form-label mt-3">{label}</Form.Label>
            <div className="p-3 border rounded bg-white shadow-sm">
                <Form.Group className="mb-3">
                    <Form.Label className="generic-form-label">{label} Title</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder={`e.g. ${label}`}
                        value={content.title || ''}
                        onChange={(e) => onChange(field, { ...content, title: e.target.value })}
                        className="generic-form-control"
                    />
                </Form.Group>

                <Form.Label className="generic-form-label">{label} Paragraphs</Form.Label>
                {paragraphs.map((paragraph: string, index: number) => (
                    <Row key={index} className="mb-2 align-items-start">
                        <Col md={10}>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder={`Paragraph ${index + 1}`}
                                value={paragraph || ''}
                                onChange={(e) => {
                                    const nextParagraphs = [...paragraphs];
                                    nextParagraphs[index] = e.target.value;
                                    updateParagraphs(nextParagraphs);
                                }}
                                className="generic-form-control"
                            />
                        </Col>
                        <Col md={2}>
                            <Button
                                variant="danger"
                                size="sm"
                                onClick={() => {
                                    const nextParagraphs = [...paragraphs];
                                    if (nextParagraphs.length > 1) {
                                        nextParagraphs.splice(index, 1);
                                    } else {
                                        nextParagraphs[0] = '';
                                    }
                                    updateParagraphs(nextParagraphs);
                                }}
                            >
                                Remove
                            </Button>
                        </Col>
                    </Row>
                ))}
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => updateParagraphs([...paragraphs, ''])}
                >
                    + Add Paragraph
                </Button>
            </div>
        </Col>
    );
};

const ProgramEligibilitySection = ({ data, onChange }: any) => {
    const eligibility = data?.eligibility || { title: '', description: '' };

    return (
        <Col md={12} className="mb-4">
            <Form.Label className="generic-form-label mt-3">Eligibility</Form.Label>
            <div className="p-3 border rounded bg-white shadow-sm">
                <Form.Group className="mb-3">
                    <Form.Label className="generic-form-label">Eligibility Title</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="e.g. Eligibility for this program"
                        value={eligibility.title || ''}
                        onChange={(e) => onChange('eligibility', { ...eligibility, title: e.target.value })}
                        className="generic-form-control"
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label className="generic-form-label">Eligibility Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={4}
                        placeholder="Describe the eligibility requirements for this program."
                        value={eligibility.description || ''}
                        onChange={(e) => onChange('eligibility', { ...eligibility, description: e.target.value })}
                        className="generic-form-control"
                    />
                </Form.Group>
            </div>
        </Col>
    );
};

const ProgramFaqSection = ({ data, onChange }: any) => {
    const faqs = data?.faqs?.length > 0 ? data.faqs : [{ question: '', answer: '' }];

    return (
        <Col md={12} className="mb-4">
            <div className="section-container border p-3 rounded bg-white shadow-sm">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h5 className="mb-0 font-weight-bold" style={{ color: '#2B3674' }}>
                            Frequently Asked Questions (FAQs)
                        </h5>
                        <small className="text-muted">Common questions and detailed answers</small>
                    </div>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => onChange('faqs', [...faqs, { question: '', answer: '' }])}
                    >
                        + Add FAQ
                    </Button>
                </div>

                {faqs.map((faq: any, index: number) => (
                    <div key={index} className="p-3 mb-3 border rounded bg-light">
                        <Row className="align-items-start">
                            <Col md={11}>
                                <Form.Group className="mb-2">
                                    <Form.Label className="small text-muted mb-1">
                                        Question ({index + 1})
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="e.g. Is accreditation important for this program?"
                                        value={faq.question || ''}
                                        onChange={(e) => {
                                            const nextFaqs = [...faqs];
                                            nextFaqs[index] = { ...nextFaqs[index], question: e.target.value };
                                            onChange('faqs', nextFaqs);
                                        }}
                                        className="generic-form-control"
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label className="small text-muted mb-1">Answer</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder="Enter the answer..."
                                        value={faq.answer || ''}
                                        onChange={(e) => {
                                            const nextFaqs = [...faqs];
                                            nextFaqs[index] = { ...nextFaqs[index], answer: e.target.value };
                                            onChange('faqs', nextFaqs);
                                        }}
                                        className="generic-form-control"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={1} className="d-flex align-items-center justify-content-end pt-2">
                                <Button
                                    variant="danger"
                                    size="sm"
                                    disabled={faqs.length <= 1}
                                    onClick={() => {
                                        const nextFaqs = [...faqs];
                                        nextFaqs.splice(index, 1);
                                        onChange('faqs', nextFaqs);
                                    }}
                                >
                                    Remove
                                </Button>
                            </Col>
                        </Row>
                    </div>
                ))}
            </div>
        </Col>
    );
};

export const ProgramForm = () => {
    const url = "/programs"
    const navigate = useNavigate();
    const { id } = useParams();

    const [universitiesList, setUniversitiesList] = useState<any[]>([]);

    useEffect(() => {
        Api.post("/universities/withPagination", { limit: 1000, page: 1 }).then((res: any) => {
            const list = res?.data?.data?.data || res?.data?.data || res?.data || [];
            if (Array.isArray(list)) {
                setUniversitiesList(list.map((u: any) => ({ label: u.name, value: u.id })));
            }
        });
    }, []);

    //Form Data
    const config: formInterface[] = [
        { type: "text", name: "Name", key: "name", require: true, md: 6 },
        { type: "text", name: "Slug", key: "slug", require: true, md: 6 },
        { type: "text", name: "Duration", key: "duration", require: false, md: 6 },
        { type: "textarea", name: "Program Description", key: "programDescription", require: false, md: 12, placeholder: "Describe this program and its academic focus." },
        { type: "fileUpload", name: "Feature Image", key: "featureImg", require: false, md: 12 },
    ];

    const [data, setData] = useState<any>();
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (id) {
            fetch(id);
        }
    }, [id]);

    const fetch = (id: any) => {
        setLoading(true);
        Api.get(`${url}/${id}`).then((res: any) => {
            let d = res?.data?.data || res?.data || res;
            if (d.universityPrograms && Array.isArray(d.universityPrograms)) {
                d.universityPrograms = d.universityPrograms.map((u: any) => ({
                    university: u.university?.id || u.university,
                    prize: u.prize || "",
                    description: u.description || ""
                }));
            }
            if (typeof d.careerOpportunities === 'string') {
                try {
                    d.careerOpportunities = JSON.parse(d.careerOpportunities);
                } catch {
                    d.careerOpportunities = null;
                }
            }
            if (!d.careerOpportunities || typeof d.careerOpportunities !== 'object') {
                d.careerOpportunities = { title: '', paragraphs: [''] };
            }
            if (!Array.isArray(d.careerOpportunities.paragraphs)) {
                d.careerOpportunities.paragraphs = d.careerOpportunities.paragraphs
                    ? [String(d.careerOpportunities.paragraphs)]
                    : [''];
            }
            if (d.careerOpportunities.paragraphs.length === 0) {
                d.careerOpportunities.paragraphs = [''];
            }
            if (typeof d.whyChoose === 'string') {
                try {
                    d.whyChoose = JSON.parse(d.whyChoose);
                } catch {
                    d.whyChoose = null;
                }
            }
            if (!d.whyChoose || typeof d.whyChoose !== 'object') {
                d.whyChoose = { title: '', paragraphs: [''] };
            }
            if (!Array.isArray(d.whyChoose.paragraphs)) {
                d.whyChoose.paragraphs = d.whyChoose.paragraphs
                    ? [String(d.whyChoose.paragraphs)]
                    : [''];
            }
            if (d.whyChoose.paragraphs.length === 0) {
                d.whyChoose.paragraphs = [''];
            }
            if (typeof d.eligibility === 'string') {
                try {
                    d.eligibility = JSON.parse(d.eligibility);
                } catch {
                    d.eligibility = null;
                }
            }
            if (!d.eligibility || typeof d.eligibility !== 'object') {
                d.eligibility = { title: '', description: '' };
            }
            if (typeof d.faqs === 'string') {
                try {
                    d.faqs = JSON.parse(d.faqs);
                } catch {
                    d.faqs = [];
                }
            }
            if (!Array.isArray(d.faqs) || d.faqs.length === 0) {
                d.faqs = [{ question: '', answer: '' }];
            } else {
                d.faqs = d.faqs.map((faq: any) => ({
                    question: faq.question || '',
                    answer: faq.answer || ''
                }));
            }
            setData(d);
        })
            .catch((e) => {
                alert(e?.message || e);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const Submit = async (e: React.FormEvent<HTMLInputElement> | any) => {
        e.preventDefault();
        const submissionData = {
            ...data,
            careerOpportunities: data?.careerOpportunities
                ? {
                    title: data.careerOpportunities.title || '',
                    paragraphs: (data.careerOpportunities.paragraphs || [])
                        .filter((paragraph: string) => paragraph && paragraph.trim().length > 0)
                }
                : null,
            whyChoose: data?.whyChoose
                ? {
                    title: data.whyChoose.title || '',
                    paragraphs: (data.whyChoose.paragraphs || [])
                        .filter((paragraph: string) => paragraph && paragraph.trim().length > 0)
                }
                : null,
            eligibility: data?.eligibility
                ? {
                    title: data.eligibility.title || '',
                    description: data.eligibility.description || ''
                }
                : null,
            faqs: (data?.faqs || [])
                .filter((faq: any) => faq.question && faq.question.trim().length > 0)
                .map((faq: any) => ({
                    question: faq.question,
                    answer: faq.answer || ''
                }))
        };
        if (id) {
            Update(submissionData);
        }
        else {
            Create(submissionData);
        }
    };

    const Update = (submissionData: any) => {
        setLoading(true);
        Api.put(`${url}/${id}`, submissionData).then((res: any) => {
            navigate(`/ProgramList`);
        })
            .catch((e) => {
                alert(e?.message || e);
            }).finally(() => {
                setLoading(false);
            });
    }

    const Create = (submissionData: any) => {
        setLoading(true);
        Api.post(url, submissionData).then((res: any) => {
            navigate(`/ProgramList`);
        })
            .catch((e) => {
                alert(e?.message || e);
            }).finally(() => {
                setLoading(false);
            });
    }

    const onChange = (key: string, value: any) => {
        let d = { ...data };
        d[key] = value;

        if (key === 'name' && value) {
            d['slug'] = value.toString().toLowerCase()
                .replace(/\s+/g, '-')           // Replace spaces with -
                .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
                .replace(/\-\-+/g, '-')         // Replace multiple - with single -
                .replace(/^-+/, '')             // Trim - from start of text
                .replace(/-+$/, '');            // Trim - from end of text
        }

        setData(d);
    };

    return (
        <Container>
            {loading && <LoadingSpinner overlay text="Saving Program..." />}
            <PageHeader
                title={id ? 'Edit Program' : 'Add New Program'}
                subtitle="Manage program profiles"
            />
            <Form onSubmit={Submit}>
                <Row>
                    {GenericForm(config, onChange, data)}

                    <Col md={12} className="mb-4">
                        <Form.Label className="generic-form-label mt-3">Career Opportunities</Form.Label>
                        <div className="p-3 border rounded bg-white shadow-sm">
                            <Form.Group className="mb-3">
                                <Form.Label className="generic-form-label">Career Opportunities Title</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g. Career Opportunities"
                                    value={data?.careerOpportunities?.title || ''}
                                    onChange={(e) => {
                                        const current = data?.careerOpportunities || { title: '', paragraphs: [''] };
                                        onChange('careerOpportunities', { ...current, title: e.target.value });
                                    }}
                                    className="generic-form-control"
                                />
                            </Form.Group>

                            <Form.Label className="generic-form-label">Career Opportunities Paragraphs</Form.Label>
                            {(data?.careerOpportunities?.paragraphs?.length > 0
                                ? data.careerOpportunities.paragraphs
                                : ['']
                            ).map((paragraph: string, index: number) => (
                                <Row key={index} className="mb-2 align-items-start">
                                    <Col md={10}>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            placeholder={`Paragraph ${index + 1}`}
                                            value={paragraph || ''}
                                            onChange={(e) => {
                                                const currentParagraphs = [
                                                    ...(data?.careerOpportunities?.paragraphs?.length > 0
                                                        ? data.careerOpportunities.paragraphs
                                                        : [''])
                                                ];
                                                currentParagraphs[index] = e.target.value;
                                                onChange('careerOpportunities', {
                                                    ...(data?.careerOpportunities || { title: '' }),
                                                    paragraphs: currentParagraphs
                                                });
                                            }}
                                            className="generic-form-control"
                                        />
                                    </Col>
                                    <Col md={2}>
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => {
                                                const currentParagraphs = [
                                                    ...(data?.careerOpportunities?.paragraphs?.length > 0
                                                        ? data.careerOpportunities.paragraphs
                                                        : [''])
                                                ];
                                                if (currentParagraphs.length > 1) {
                                                    currentParagraphs.splice(index, 1);
                                                } else {
                                                    currentParagraphs[0] = '';
                                                }
                                                onChange('careerOpportunities', {
                                                    ...(data?.careerOpportunities || { title: '' }),
                                                    paragraphs: currentParagraphs
                                                });
                                            }}
                                        >
                                            Remove
                                        </Button>
                                    </Col>
                                </Row>
                            ))}
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                    const currentParagraphs = [
                                        ...(data?.careerOpportunities?.paragraphs?.length > 0
                                            ? data.careerOpportunities.paragraphs
                                            : [''])
                                    ];
                                    currentParagraphs.push('');
                                    onChange('careerOpportunities', {
                                        ...(data?.careerOpportunities || { title: '' }),
                                        paragraphs: currentParagraphs
                                    });
                                }}
                            >
                                + Add Paragraph
                            </Button>
                        </div>
                    </Col>

                    <ProgramContentSection
                        data={data}
                        onChange={onChange}
                        field="whyChoose"
                        label="Why Choose this Specialisation?"
                    />

                    <ProgramEligibilitySection data={data} onChange={onChange} />

                    <ProgramFaqSection data={data} onChange={onChange} />

                    <SeoFormSection data={data} onChange={onChange} />

                    <Col md={12} className="mb-4">
                        <Form.Label className="generic-form-label mt-3">Universities & Programs Info</Form.Label>
                        {(data?.universityPrograms || []).map((up: any, index: number) => (
                            <div key={index} className="p-3 mb-3 border rounded bg-light">
                                <Row className="align-items-center mb-2">
                                    <Col md={5}>
                                        <Form.Label className="small text-muted mb-1">University</Form.Label>
                                        <Form.Select
                                            value={up.university}
                                            onChange={(e) => {
                                                const newUP = [...(data.universityPrograms || [])];
                                                newUP[index].university = e.target.value;
                                                onChange('universityPrograms', newUP);
                                            }}
                                            className="generic-form-control"
                                        >
                                            <option value="">Select University...</option>
                                            {universitiesList.map(u => (
                                                <option key={u.value} value={u.value}>{u.label}</option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                    <Col md={5}>
                                        <Form.Label className="small text-muted mb-1">Prize / Starting Fee</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={0}
                                            placeholder="e.g. 75000"
                                            value={up.prize ?? ''}
                                            onChange={(e) => {
                                                const newUP = [...(data.universityPrograms || [])];
                                                newUP[index].prize = e.target.value === '' ? null : Number(e.target.value);
                                                onChange('universityPrograms', newUP);
                                            }}
                                            className="generic-form-control"
                                        />
                                    </Col>
                                    <Col md={2} className="d-flex align-items-end mt-4">
                                        <Button variant="danger" size="sm" onClick={() => {
                                            const newUP = [...(data.universityPrograms || [])];
                                            newUP.splice(index, 1);
                                            onChange('universityPrograms', newUP);
                                        }}>Remove</Button>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={12}>
                                        <Form.Label className="small text-muted mb-1">Program Description</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            placeholder="Description of this program for this university..."
                                            value={up.description || ''}
                                            onChange={(e) => {
                                                const newUP = [...(data.universityPrograms || [])];
                                                newUP[index].description = e.target.value;
                                                onChange('universityPrograms', newUP);
                                            }}
                                            className="generic-form-control"
                                        />
                                    </Col>
                                </Row>
                            </div>
                        ))}
                        <div className="mt-2">
                            <Button variant="secondary" size="sm" onClick={() => {
                                const newUP = [...(data?.universityPrograms || [])];
                                newUP.push({ university: '', prize: null, description: '' });
                                onChange('universityPrograms', newUP);
                            }}>+ Add University</Button>
                        </div>
                    </Col>

                    <Col md={12}>
                        <Button className="mt-4 professional-submit-btn" type="submit">
                            Submit
                        </Button>
                    </Col>
                </Row>
            </Form>
        </Container>
    );
};
